<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-12">
        <div v-if="loading" class="text-center text-muted mb-4">
          <font-awesome-icon icon="spinner" spin class="mr-2"/>
          Loading job details...
        </div>
        <div v-else-if="error" class="text-danger text-center mb-4">
          {{ error }}
        </div>
        <div v-else-if="jobs.length > 0">
          <div v-for="job in jobs" :key="job.id" class="row justify-content-center mb-4">
            <div class="col-12 col-md-8 col-lg-6">
              <div class="card">
                <div class="card-body">
                  <Share
                    :url="getJobShareUrl(job)"
                    :title="getJobTitle(job)"
                    :preview-text="jobShareText"
                    :preview-args="{ jobTitle: job.title }"
                    :providers="canShareJobsWith"
                    :source="'job'"
                    :centered="true"
                    @track="(data) => trackJobSharedEvent(data, job)"
                    @openLinkedInJobConnectionModal="() => handleLinkedInConnectionModal(job.id)"
                    @openLinkedInJobsModal="() => handleLinkedInJobsModal(job.id)"
                  >
                    <template #text>
                      <div class="mb-3">
                        <h5 class="mb-2 d-none">{{ job.title }}</h5>
                        <p v-if="company" class="text-muted">{{ company.name }}</p>
                        <p v-if="job.location" class="text-muted">
                          <font-awesome-icon icon="map-marker-alt" class="mr-1"/>
                          {{ job.location.name }}
                        </p>
                      </div>
                    </template>
                  </Share>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Connect LinkedIn Modals -->
    <b-modal v-for="job in jobs" :key="`connect-${job.id}`" v-if="hasJobShareV2Enabled" title="Connect LinkedIn" :id="`connect-jobs-linkedin-${job.id}`" hide-footer @close="cleanupRoute()">
      <ConnectLinkedIn
        :entryID="job.id"
      />
    </b-modal>

    <!-- LinkedIn Post Editor Modals -->
    <b-modal v-for="job in jobs" :key="`editor-${job.id}`" v-if="hasJobShareV2Enabled" :id="`job-share-linkedin-post-editor-${job.id}`" hide-footer title="Edit LinkedIn Post" no-close-on-backdrop no-close-on-esc @close="cleanupRoute()">
      <LinkedInJobsPostEditor
        :job="{...job, url: getJobShareUrl(job)}"
        :campaignID="job && job.campaigns && job.campaigns[0] && job.campaigns[0].id"
        @track="(data) => trackJobSharedEvent(data, job)"
      />
    </b-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Share from '@/components/Share.vue';
import LinkedInJobsPostEditor from '@/components/content-share/LinkedInJobsPostEditor';
import ConnectLinkedIn from '@/components/content-share/ConnectLinkedIn';
import { mapRoot, mapPreferences } from '@src/mixins/root';

const { default: confetti } = process.env.VUE_ENV === 'client' ? require('canvas-confetti') : null;

const GET_JOBS_QUERY = gql`
  query GetJobs($jobIDs: [ID!]!) {
    company {
      id
      name
      jobs(where: { id_in: $jobIDs }) {
        id
        title
        location {
          id
          name
        }
        shareCode
        campaigns {
          id
          title
        }
      }
    }
  }
`;

export default {
  name: 'EngagementShareJob',
  components: {
    Share,
    LinkedInJobsPostEditor,
    ConnectLinkedIn,
  },
  props: {
    jobIDs: {
      type: String,
      required: true,
    },
  },
  mixins: [
    mapRoot([ 'company', 'user', 'origin', 'isAssurant', 'isHelloFresh' ])
  ],
  data() {
    return {
      error: null,
    };
  },
  async mounted() {
    const { state } = this.user || {};
    const { linkedIn } = state || {};
    const { accessToken, expiresAt, version } = linkedIn || {};
    const now = new Date().getTime();
    const isLinkedInConnected = accessToken && expiresAt && expiresAt > now && version === 'v1';

    if (this.$route.query && this.$route.query.j && this.jobIDArray.includes(this.$route.query.j)) {
      if (isLinkedInConnected) {
        this.$bvModal.show(`job-share-linkedin-post-editor-${this.$route.query.j}`);
      } else {
        this.$bvModal.show(`connect-jobs-linkedin-${this.$route.query.j}`);
      }
    }

    if (this.$route.query && this.$route.query.callback && !this.$route.hash.includes('error')) {
      const jobID = this.$route.query.callback.split(':')[1];

      if (jobID && this.jobIDArray.includes(jobID)) {
        this.$bvModal.show(`job-share-linkedin-post-editor-${jobID}`);
      }
    }
  },
  events: {
    capturedLog({ eventName }) {
      if ([ 'JOB_SHARED', 'CONTENT_SHARED' ].includes(eventName)) {
        confetti({ particleCount: 150, spread: 180, zIndex: 1200 });
      }
    },
  },
  apollo: {
    jobsData: {
      query: GET_JOBS_QUERY,
      variables() {
        return { jobIDs: this.jobIDArray };
      },
      update: ({ company }) => company,
      error(err) {
        console.error('Error loading jobs:', err);
        this.error = 'Failed to load job details';
      },
    },
  },
  computed: {
    jobIDArray() {
      return this.jobIDs.split(',').filter(id => id.trim());
    },
    ...mapRoot([ 'company', 'user' ]),
    loading() {
      return this.$apollo.queries.jobsData.loading;
    },
    jobs() {
      return this.jobsData && this.jobsData.jobs ? this.jobsData.jobs : [];
    },
    company() {
      return this.jobsData || null;
    },
    ...mapRoot({
      canShareJobsWith({ company }) {
        const { preferences, sitePreferences, sharePreferences } = company || {};
        const { canShareJobsWith } = { ...preferences, ...sitePreferences, ...sharePreferences };
        return canShareJobsWith;
      },
      jobShareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { customFieldsHash: customFields } = this.primaryJob || {};
        const { jobShareText } = { ...preferences, ...sitePreferences, ...sharePreferences, ...customFields };

        if (this.isAssurant && this.user && this.user.locale === 'fr') {
          return 'Assurant recrute ! Nous recherchons un(e) {jobTitle} pour rejoindre notre équipe. Consultez l\'annonce et postulez maintenant !';
        }

        if (this.isHelloFresh && this.user && this.user.locale === 'de') {
          return 'Hast du Lust mit mir bei HelloFresh als {jobTitle} zu arbeiten? Jetzt bewerben!';
        }

        return jobShareText || 'Are you interested in working with me as a {jobTitle}?';
      },
    }),
    ...mapPreferences([ 'featuresEnabled' ]),
    hasJobShareV2Enabled() {
      return (this.featuresEnabled || []).includes('job-share-v2');
    },
    hasFacebookEnabled() {
      return (this.featuresEnabled || []).includes('facebook-share-v2');
    },
  },
  methods: {
    getJobShareUrl(job) {
      return `${window.location.origin}/share/${job.shareCode}`;
    },
    getJobTitle(job) {
      return `${this.$t('contentShares.card.share-cta')}: ${job.title}`;
    },
    handleLinkedInConnectionModal(jobID) {
      this.$bvModal.show(`connect-jobs-linkedin-${jobID}`);
    },
    handleLinkedInJobsModal(jobID) {
      this.$bvModal.show(`job-share-linkedin-post-editor-${jobID}`);
    },
    async trackJobSharedEvent({ channel }, job) {
      try {
        const { id: jobID, campaigns } = job || {};
        const { id: campaignID } = (campaigns && campaigns[0]) || {};
        await this.$root.captureLog({
          eventName: 'JOB_SHARED',
          campaign: campaignID ? { id: campaignID } : undefined,
          job: { id: jobID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    cleanupRoute() {
      if (this.$router.history && this.$router.history.current && this.$router.history.current.query) {
        // refresh the page if we open the card from a comm message
        if (this.$router.history.current.query.j || (this.$router.history.current.query.callback && this.$router.history.current.query.callback.startsWith('connectLinkedInForPosting'))) {
          this.$router.push(this.$router.history.current.path);
          location.reload();
        } else {
          this.$router.push(this.$router.history.current.path);
        }
      }
    }
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-body {
  padding: 2rem;
}
</style>



// WEBPACK FOOTER //
// src/views/engagement/ShareJob.vue